import { getChatSession, sendChatMessage } from '../../api/chatApi';
import { ChatConnectionError, isNetworkFailureResponse, sendTurnLanded } from './chatConnectionError';
import { ensureOkResponse } from './chatMessageModel';
import { STALLED_TURN_MS } from './useStalledChatTurns';

function isJoblessProcessing(message) {
  return message?.job_status === 'processing' && !message.job_id;
}

function landedAssistant(session, text) {
  const history = session.messages || [];
  const userIndex = history.map((message) => message.role === 'user'
    && String(message.content || '').trim() === text.trim()).lastIndexOf(true);
  return history.slice(userIndex + 1).find((message) => message.role === 'assistant');
}

/**
 * Post one chat turn. When the browser loses the reply (FE #746), the server may
 * still have accepted the turn, so re-read the session before calling it failed:
 * resolves `{ landedSession }` when the turn is there, `{ data }` on a normal reply,
 * and throws a ChatConnectionError when neither can be confirmed.
 */
export async function submitChatTurn({ sessionId, text, idempotencyKey, priorMessages, isCancelled = () => false }) {
  const response = await sendChatMessage(sessionId, text, idempotencyKey);
  if (isCancelled()) return {};
  if (!isNetworkFailureResponse(response)) {
    return { data: ensureOkResponse(response, 'Unable to submit chat message.') };
  }

  const sessionResponse = await getChatSession(sessionId);
  if (isNetworkFailureResponse(sessionResponse) || !sessionResponse || sessionResponse.status >= 400) {
    throw new ChatConnectionError();
  }
  const sessionData = sessionResponse.data || {};
  if (sessionData.session_id === sessionId && sendTurnLanded(sessionData.messages, priorMessages, text)) {
    const assistant = landedAssistant(sessionData, text);
    return { landedSession: sessionData, pendingMessage: isJoblessProcessing(assistant) ? assistant : null };
  }
  throw new ChatConnectionError();
}

// A lost POST can leave a saved user turn whose answer is still being produced.
// Read serially; an independent deadline also closes out a GET that never settles.
export function reconcileChatTurn({ sessionId, pendingMessage, startedAt, isCancelled, onResolved, onStalled }) {
  let stopped = false;
  let pollTimer;
  let deadlineTimer;
  let finish;
  const done = new Promise((resolve) => { finish = resolve; });
  const stop = () => {
    stopped = true;
    clearTimeout(pollTimer);
    clearTimeout(deadlineTimer);
    finish();
  };
  const cancelled = () => stopped || isCancelled();
  deadlineTimer = setTimeout(() => {
    if (!cancelled()) onStalled();
    stop();
  }, Math.max(0, startedAt + STALLED_TURN_MS - Date.now()));

  async function read() {
    if (cancelled()) { stop(); return; }
    try {
      const response = await getChatSession(sessionId);
      if (cancelled()) { stop(); return; }
      const data = ensureOkResponse(response, 'Unable to load chat session.');
      const assistant = (data.messages || []).find((message) => message.role === 'assistant'
        && message.submission_id === pendingMessage.submission_id);
      if (data.session_id === sessionId && assistant && !isJoblessProcessing(assistant)) {
        clearTimeout(deadlineTimer);
        await onResolved(data, cancelled);
        stop();
        return;
      }
    } catch {
      // A dropped re-read does not undo the confirmed send or offer another POST.
    }
    if (cancelled()) stop();
    else pollTimer = setTimeout(read, 1000);
  }
  pollTimer = setTimeout(read, 1000);
  return { done, stop };
}
